import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { InfoRow, MetricCard } from '../components/servers/ServerWidgets';
import {
  BackLink,
  HitsTable,
  IdentityCard,
  Section,
  VerdictBanner,
  backendLabel,
  eventSummary,
  formatWhen,
} from '../components/security/SecurityDetail';
import { statusLabel } from '../components/security/securityLabels';
import {
  apiGetCybersecurityMonitoredDetail,
  apiRevertCybersecurityMonitored,
} from '../api/client';

export default function SecurityMonitoredDetailPage() {
  const { source, id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      setData(await apiGetCybersecurityMonitoredDetail(source, id));
    } catch (e) {
      setError(e.message || 'Failed to load monitored actor');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [source, id]);

  const onRevert = async () => {
    try {
      await apiRevertCybersecurityMonitored(source, id);
      await load();
    } catch (e) {
      setError(e.message || 'Revert failed');
    }
  };

  const row = data?.monitored;

  return (
    <AppLayout>
      <div className="page-content">
        <BackLink />
        {error && <p style={{ color: '#b91c1c' }}>{error}</p>}
        {loading && <p style={{ color: '#64748b' }}>Loading monitored actor…</p>}
        {!loading && row && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', margin: '12px 0 8px' }}>
              <div>
                <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>{row.actor_key || `user ${row.user_id}`}</h1>
                <p style={{ color: '#64748b', marginTop: 4 }}>{backendLabel(source)} · watch window</p>
              </div>
              {row.status === 'active' && (
                <button type="button" onClick={onRevert} style={{ padding: '8px 12px', borderRadius: 8, border: 0, background: '#0f766e', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                  Return to normal
                </button>
              )}
            </div>
            <VerdictBanner
              level={row.status === 'active' ? 'warning' : 'info'}
              text={`${statusLabel(row.status)} · ${row.reason || 'security watch'}`}
            />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 8 }}>
              <MetricCard label="Started" value={formatWhen(row.started_at)} />
              <MetricCard label="Expires" value={formatWhen(row.expires_at)} />
              <MetricCard label="Status" value={statusLabel(row.status)} />
            </div>
            <IdentityCard user={data.user} />
            <Section title="Watch window">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
                <InfoRow label="Reason" value={row.reason} />
                <InfoRow label="Who" value={row.actor_key} />
                <InfoRow label="User ID" value={row.user_id} />
              </div>
            </Section>
            <Section title="Requests during watch">
              <HitsTable hits={data.hits} source={source} />
            </Section>
            <Section title="Related events">
              {(data.events || []).length === 0 && <p style={{ color: '#64748b', margin: 0 }}>No events.</p>}
              {(data.events || []).map((event) => (
                <button
                  key={event.id}
                  type="button"
                  onClick={() => navigate(`/cybersecurity/event/${source}/${event.id}`)}
                  style={{
                    display: 'flex', justifyContent: 'space-between', width: '100%', textAlign: 'left',
                    padding: '10px 0', border: 0, borderBottom: '1px solid #f1f5f9', background: 'transparent', cursor: 'pointer',
                  }}
                >
                  <strong>{eventSummary(event)}</strong>
                  <span style={{ color: '#64748b' }}>{formatWhen(event.created_at)}</span>
                </button>
              ))}
            </Section>
          </>
        )}
      </div>
    </AppLayout>
  );
}
